import {
  Notification,
  NotificationSettings,
  SensitivityType,
  AlertSound,
  AlertType,
  GoalsNotification,
} from "@observe-tech/utils";

export interface NotificationTypeHandler {
  isEnabled: (settings?: NotificationSettings) => boolean;
  isMutedForType: (settings?: NotificationSettings) => boolean;
  meetsThreshold: (
    notification: Notification,
    settings?: NotificationSettings,
  ) => boolean;
  getSound: (settings?: NotificationSettings) => AlertSound;
  getAlertType: (settings?: NotificationSettings) => AlertType;
}

export type NotificationTypeHandlerConfig = {
  enabledPath: keyof NotificationSettings;
  soundPath: keyof NotificationSettings;
  alertTypePath: keyof NotificationSettings;
  defaultSound?: AlertSound;
  defaultAlertType?: AlertType;
  thresholdCheck?: (
    notification: Notification,
    settings?: NotificationSettings,
  ) => boolean;
};

const checkGoalNotificationThreshold = (
  notification: GoalsNotification,
  settings_sensitivity: SensitivityType | undefined,
) => {
  if (!settings_sensitivity) return false;
  const goal_sensitivity = notification.metadata?.sensitivity;
  if (settings_sensitivity == "HIGH" && goal_sensitivity == "HIGH") return true;
  if (settings_sensitivity == "MEDIUM" && goal_sensitivity != "LOW")
    return true;
  if (settings_sensitivity == "LOW") return true;
  return false;
};

const getNotificationKey = (notification: Notification): string => {
  return `${notification.type}:${notification.subtype}`;
};

const createNotificationTypeHandler = (
  config: NotificationTypeHandlerConfig,
): NotificationTypeHandler => ({
  isEnabled: (settings) => {
    return settings?.[config.enabledPath] !== false;
  },

  isMutedForType: (settings) => {
    return settings?.[config.enabledPath] === false;
  },

  meetsThreshold: config.thresholdCheck || ((settings) => true),

  // `defaultSound` is NOT the product default — DEFAULT_SETTINGS is, and it
  // carries an alertSound for every path here, so a desktop with nothing saved
  // resolves through the first branch and rings. This fallback is reached only
  // when a saved settings blob is MISSING this key, which happens when an alert
  // is renamed or added after the operator last saved (e.g. the E-stop ->
  // winchManualControl rename in DEV-2651). Sound is intrusive, so an alert the
  // operator has never been offered stays silent until they configure it —
  // raising these to BELL would un-mute alerts somebody deliberately muted
  // under the old key.
  getSound: (settings) => {
    return (
      (settings?.[config.soundPath] as AlertSound) ||
      config.defaultSound ||
      "BELL"
    );
  },

  getAlertType: (settings) => {
    return (
      (settings?.[config.alertTypePath] as AlertType) ||
      config.defaultAlertType ||
      "ROW"
    );
  },
});

const NOTIFICATION_HANDLERS: Record<string, NotificationTypeHandler> = {
  "benchmark:pellets_threshold_exceeded": createNotificationTypeHandler({
    enabledPath: "feedProfile.pelletAlerts.enabled",
    soundPath: "feedProfile.pelletAlerts.alertSound",
    alertTypePath: "feedProfile.pelletAlerts.alertType",
    defaultSound: "BELL",
    defaultAlertType: "ROW",
    thresholdCheck: (notification, settings) => {
      const sensitivity = settings?.["feedProfile.pelletAlerts.sensitivity"];
      const pelletCount = notification.metadata?.nPellets;

      if (typeof sensitivity !== "number" || typeof pelletCount !== "number") {
        return true;
      }

      return pelletCount >= sensitivity;
    },
  }),
  "equipment:unresponsive_winch_system": createNotificationTypeHandler({
    enabledPath: "equipment.winch.enabled",
    soundPath: "equipment.winch.alertSound",
    alertTypePath: "equipment.winch.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
  }),
  "equipment:winch_manual_control": createNotificationTypeHandler({
    enabledPath: "equipment.winchManualControl.enabled",
    soundPath: "equipment.winchManualControl.alertSound",
    alertTypePath: "equipment.winchManualControl.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
  }),
  "equipment:unresponsive_feed_system": createNotificationTypeHandler({
    enabledPath: "equipment.feedSystem.enabled",
    soundPath: "equipment.feedSystem.alertSound",
    alertTypePath: "equipment.feedSystem.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
  }),
  "goal:feeding_response": createNotificationTypeHandler({
    enabledPath: "feeding.overfeeding.feedingResponse.enabled",
    soundPath: "feeding.overfeeding.feedingResponse.alertSound",
    alertTypePath: "feeding.overfeeding.feedingResponse.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
    thresholdCheck: (notification, settings) => {
      return checkGoalNotificationThreshold(
        notification as GoalsNotification,
        settings?.["feeding.overfeeding.feedingResponse.sensitivity"],
      );
    },
  }),
  "goal:limit_grazing": createNotificationTypeHandler({
    enabledPath: "feeding.underfeeding.limitGrazing.enabled",
    soundPath: "feeding.underfeeding.limitGrazing.alertSound",
    alertTypePath: "feeding.underfeeding.limitGrazing.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
    thresholdCheck: (notification, settings) => {
      return checkGoalNotificationThreshold(
        notification as GoalsNotification,
        settings?.["feeding.underfeeding.limitGrazing.sensitivity"],
      );
    },
  }),
  "goal:feed_profile_smoothness": createNotificationTypeHandler({
    enabledPath: "feeding.standardization.feedProfileSmoothness.enabled",
    soundPath: "feeding.standardization.feedProfileSmoothness.alertSound",
    alertTypePath: "feeding.standardization.feedProfileSmoothness.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
    thresholdCheck: (notification, settings) => {
      return checkGoalNotificationThreshold(
        notification as GoalsNotification,
        settings?.["feeding.standardization.feedProfileSmoothness.sensitivity"],
      );
    },
  }),
  "goal:camera_movement": createNotificationTypeHandler({
    enabledPath: "feeding.standardization.cameraMovement.enabled",
    soundPath: "feeding.standardization.cameraMovement.alertSound",
    alertTypePath: "feeding.standardization.cameraMovement.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
    thresholdCheck: (notification, settings) => {
      return checkGoalNotificationThreshold(
        notification as GoalsNotification,
        settings?.["feeding.standardization.cameraMovement.sensitivity"],
      );
    },
  }),
  "automation:automation_event_triggered": createNotificationTypeHandler({
    enabledPath: "copilot.automaticChanges.enabled",
    soundPath: "copilot.automaticChanges.alertSound",
    alertTypePath: "copilot.automaticChanges.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
  }),
  "automation:automation_on_demand_failure": createNotificationTypeHandler({
    enabledPath: "copilot.quickActions.failedToExecute.enabled",
    soundPath: "copilot.quickActions.failedToExecute.alertSound",
    alertTypePath: "copilot.quickActions.failedToExecute.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
  }),
  "environment:oxygen_threshold_below": createNotificationTypeHandler({
    enabledPath: "environment.oxygen.enabled",
    soundPath: "environment.oxygen.alertSound",
    alertTypePath: "environment.oxygen.alertType",
    defaultSound: "BELL",
    defaultAlertType: "ROW",
  }),
  "environment:oxygen_forecast_below": createNotificationTypeHandler({
    enabledPath: "environment.oxygenForecast.enabled",
    soundPath: "environment.oxygenForecast.alertSound",
    alertTypePath: "environment.oxygenForecast.alertType",
    defaultSound: "MUTE",
    defaultAlertType: "ROW",
  }),
  "environment:saturation_threshold_below": createNotificationTypeHandler({
    enabledPath: "environment.oxygenSaturation.enabled",
    soundPath: "environment.oxygenSaturation.alertSound",
    alertTypePath: "environment.oxygenSaturation.alertType",
    defaultSound: "BELL",
    defaultAlertType: "ROW",
  }),
  "environment:temperature_threshold_below": createNotificationTypeHandler({
    enabledPath: "environment.temperature.enabled",
    soundPath: "environment.temperature.alertSound",
    alertTypePath: "environment.temperature.alertType",
    defaultSound: "BELL",
    defaultAlertType: "ROW",
  }),
  "environment:salinity_threshold_below": createNotificationTypeHandler({
    enabledPath: "environment.salinity.enabled",
    soundPath: "environment.salinity.alertSound",
    alertTypePath: "environment.salinity.alertType",
    defaultSound: "BELL",
    defaultAlertType: "ROW",
  }),
  "environment:temperature_threshold_above": createNotificationTypeHandler({
    enabledPath: "environment.temperature.enabled",
    soundPath: "environment.temperature.alertSound",
    alertTypePath: "environment.temperature.alertType",
    defaultSound: "BELL",
    defaultAlertType: "ROW",
  }),
  "environment:saturation_temperature_model_NORWAY":
    createNotificationTypeHandler({
      enabledPath: "environment.saturationTemperatureModel.norway.enabled",
      soundPath: "environment.saturationTemperatureModel.norway.alertSound",
      alertTypePath: "environment.saturationTemperatureModel.norway.alertType",
      defaultSound: "BELL",
      defaultAlertType: "ROW",
    }),
  "environment:saturation_temperature_model_CHILE":
    createNotificationTypeHandler({
      enabledPath: "environment.saturationTemperatureModel.chile.enabled",
      soundPath: "environment.saturationTemperatureModel.chile.alertSound",
      alertTypePath: "environment.saturationTemperatureModel.chile.alertType",
      defaultSound: "BELL",
      defaultAlertType: "ROW",
    }),
};

const isNotificationMuted = (
  notification: Notification,
  notificationSettings?: NotificationSettings,
): boolean => {
  if (notificationSettings?.muteForToday === true) {
    return true;
  }

  const key = getNotificationKey(notification);
  const handler = NOTIFICATION_HANDLERS[key]?.isMutedForType;

  if (handler) {
    return handler(notificationSettings);
  }

  return false;
};

const getNotificationSound = (
  notification: Notification,
  notificationSettings?: NotificationSettings,
): AlertSound => {
  const key = getNotificationKey(notification);
  const handler = NOTIFICATION_HANDLERS[key]?.getSound;
  return handler ? handler(notificationSettings) : "BELL";
};

const shouldFilterByThreshold = (
  notification: Notification,
  notificationSettings?: NotificationSettings,
): boolean => {
  const key = getNotificationKey(notification);
  const handler = NOTIFICATION_HANDLERS[key]?.meetsThreshold;

  if (!handler) {
    return false;
  }

  return !handler(notification, notificationSettings);
};

const getAlertType = (
  notification: Notification,
  notificationSettings?: NotificationSettings,
): AlertType => {
  const key = getNotificationKey(notification);
  const handler = NOTIFICATION_HANDLERS[key]?.getAlertType;
  return handler ? handler(notificationSettings) : "ROW";
};

const getCritical = (
  notification: Notification,
  notificationSettings?: NotificationSettings,
): boolean => {
  const key = getNotificationKey(notification);
  const handler = NOTIFICATION_HANDLERS[key];

  if (!handler) {
    return false;
  }

  const criticalPath = getCriticalPathForNotification(key);
  if (!criticalPath || !notificationSettings) {
    return false;
  }

  return notificationSettings[criticalPath] === true;
};

const getCriticalPathForNotification = (
  key: string,
): keyof NotificationSettings | null => {
  const criticalPaths: Record<string, keyof NotificationSettings> = {
    "benchmark:pellets_threshold_exceeded":
      "feedProfile.pelletAlerts.isCritical",
    "equipment:unresponsive_winch_system": "equipment.winch.isCritical",
    "equipment:winch_manual_control": "equipment.winchManualControl.isCritical",
    "equipment:unresponsive_feed_system": "equipment.feedSystem.isCritical",
    "goal:feeding_response": "feeding.overfeeding.feedingResponse.isCritical",
    "goal:limit_grazing": "feeding.underfeeding.limitGrazing.isCritical",
    "goal:feed_profile_smoothness":
      "feeding.standardization.feedProfileSmoothness.isCritical",
    "goal:camera_movement": "feeding.standardization.cameraMovement.isCritical",
    "automation:automation_event_triggered":
      "copilot.automaticChanges.isCritical",
    "automation:automation_on_demand_failure":
      "copilot.quickActions.failedToExecute.isCritical",
    "environment:oxygen_threshold_below": "environment.oxygen.isCritical",
    "environment:oxygen_forecast_below":
      "environment.oxygenForecast.isCritical",
    "environment:saturation_threshold_below":
      "environment.oxygenSaturation.isCritical",
    "environment:temperature_threshold_below":
      "environment.temperature.isCritical",
    "environment:salinity_threshold_below": "environment.salinity.isCritical",
    "environment:temperature_threshold_above":
      "environment.temperature.isCritical",
    "environment:saturation_temperature_model_NORWAY":
      "environment.saturationTemperatureModel.norway.isCritical",
    "environment:saturation_temperature_model_CHILE":
      "environment.saturationTemperatureModel.chile.isCritical",
  };

  return criticalPaths[key] || null;
};

export const notificationUtils = {
  isNotificationMuted,
  getNotificationSound,
  shouldFilterByThreshold,
  getAlertType,
  getCritical,
};
